import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import ContactForm from "@/components/site/ContactForm";
import { MODULES } from "@/lib/site-data";
import { ArrowUpRight, Workflow, Plug, ShieldCheck } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1631815587646-b85a1bb027e1?crop=entropy&cs=tinysrgb&w=1600&q=80";

export default function ModulesPage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <main data-testid="modules-page" className="bg-ab-bg text-ab-text">
      <Navbar />

      {/* HERO */}
      <section ref={ref} className="relative pt-36 md:pt-44 pb-24 overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-ab-bg via-[#EFEDE6] to-ab-bg" />
          <div className="absolute top-20 right-0 w-[520px] h-[520px] rounded-full bg-ab-accent/10 blur-3xl" />
        </motion.div>

        <div className="ab-container">
          <p className="ab-eyebrow text-ab-accent">Modules</p>
          <h1 className="ab-h1 text-6xl md:text-8xl mt-5 max-w-4xl">
            Every part of your hospital, <span className="italic font-light text-ab-accent">in one OS</span>.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-ab-muted leading-relaxed">
            Twelve modules, one runtime. Pick what your hospital needs today; the OS quietly handles the rest as you grow.
          </p>
        </div>
      </section>

      {/* BENTO GRID */}
      <section className="relative pb-24">
        <div className="ab-container grid grid-cols-12 gap-5">
          {MODULES.map((m, i) => {
            const Icon = m.icon;
            const span = SPANS[i % SPANS.length];
            return (
              <motion.article
                key={m.id}
                data-testid={`bento-${m.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ delay: (i % 6) * 0.05 }}
                className={`ab-card relative overflow-hidden p-7 md:p-8 flex flex-col ${span} group`}
              >
                {span.includes("row-span-2") && (
                  <div className="absolute inset-0 -z-0 opacity-90">
                    <img src={m.image} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-white/85 to-white/30" />
                  </div>
                )}
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <span className="w-12 h-12 rounded-full bg-ab-bg flex items-center justify-center text-ab-primary group-hover:bg-ab-primary group-hover:text-white transition">
                      <Icon className="w-5 h-5" />
                    </span>
                    <span className="font-mono-tech text-xs text-ab-muted">M.{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="mt-6 font-serif text-2xl md:text-3xl text-ab-primary leading-tight">{m.title}</h3>
                  <p className="mt-2 text-ab-muted leading-relaxed text-sm">{m.summary}</p>
                  <ul className="mt-4 space-y-1.5 text-sm text-ab-text/85">
                    {m.bullets.map((b, idx) => (
                      <li key={idx} className="flex items-start gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-ab-accent shrink-0" />{b}</li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* WORKFLOW STRIP */}
      <section id="workflow" className="relative bg-ab-primary text-white py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 ab-grain opacity-30" />
        <div className="ab-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7">
            <p className="ab-eyebrow text-ab-accent">Custom workflows</p>
            <h2 className="ab-h2 text-5xl md:text-6xl mt-4 text-white">
              Your hospital. <span className="italic font-light">Your</span> rules.
            </h2>
            <p className="mt-6 max-w-xl text-white/75 leading-relaxed">
              Configure approvals, escalations and SLAs without forking the product. Wire PranaOs to your LIS, RIS, accounting and government rails — using a workflow studio designed for clinical operations.
            </p>
            <a href="#contact" data-testid="modules-workflow-cta" className="mt-10 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white text-ab-primary hover:bg-ab-accent hover:text-white transition">
              Talk integrations <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {[
              { Icon: Workflow, t: "Drag-drop builder" },
              { Icon: Plug, t: "HL7 / FHIR adapters" },
              { Icon: ShieldCheck, t: "Role-aware approvals" },
              { Icon: ArrowUpRight, t: "SLA-bound queues" },
            ].map(({ Icon, t }, i) => (
              <div key={i} className="rounded-2xl border border-white/15 p-5 bg-white/5 backdrop-blur-sm">
                <Icon className="w-5 h-5 text-ab-accent" />
                <p className="mt-3 font-serif text-xl">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactForm />
      <Footer />
    </main>
  );
}

const SPANS = [
  "col-span-12 md:col-span-7 row-span-2 min-h-[420px]",
  "col-span-12 md:col-span-5 min-h-[200px]",
  "col-span-12 md:col-span-5 min-h-[200px]",
  "col-span-12 md:col-span-4 min-h-[220px]",
  "col-span-12 md:col-span-4 min-h-[220px]",
  "col-span-12 md:col-span-4 min-h-[220px]",
  "col-span-12 md:col-span-5 row-span-2 min-h-[420px]",
  "col-span-12 md:col-span-7 min-h-[200px]",
  "col-span-12 md:col-span-3 min-h-[220px]",
  "col-span-12 md:col-span-4 min-h-[220px]",
  "col-span-12 md:col-span-5 min-h-[220px]",
  "col-span-12 md:col-span-12 min-h-[200px]",
];
